/**
 * Property Map Component
 * 부동산 위치 지도 표시
 */

import { useEffect, useRef, useState } from "react";
import { loadGoogleMaps } from "@/lib/googleMapsLoader";

interface PropertyMapProps {
  lat?: string | null;
  lng?: string | null;
  address?: string | null;
  city?: string;
  suburb?: string | null;
  state?: string;
}

declare global {
  interface Window {
    google: any;
  }
}

export function PropertyMap({ lat, lng, address, city, suburb, state }: PropertyMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if Google Maps is already loaded
    if (window.google?.maps?.Map && window.google?.maps?.Marker) {
      setMapsLoaded(true);
      return;
    }

    // Use centralized Google Maps loader
    let cancelled = false;

    import("@/lib/googleMapsLoader")
      .then(({ loadGoogleMaps }) => {
        if (cancelled) return;

        return loadGoogleMaps()
          .then(() => {
            if (cancelled) return;

            // 다시 확인하여 안전하게 설정
            if (window.google?.maps?.Map && window.google?.maps?.Marker) {
              setMapsLoaded(true);
            } else {
              console.warn("Google Maps loaded but Map/Marker not available");
              setError("지도 기능을 사용할 수 없습니다");
            }
          })
          .catch((error) => {
            if (cancelled) return;
            console.warn("Failed to load Google Maps:", error);
            setError("지도를 불러올 수 없습니다");
          });
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Failed to import googleMapsLoader:", error);
        setError("지도를 불러올 수 없습니다");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current || !mapsLoaded) return;

    // Google Maps가 로드되었는지 확인
    if (!window.google?.maps) {
      console.warn("Google Maps API not loaded yet");
      return;
    }

    try {
      const Map = window.google.maps.Map;
      const Marker = window.google.maps.Marker;

      if (!Map || !Marker) {
        console.warn("Google Maps Map or Marker not available");
        setError("지도 기능을 사용할 수 없습니다");
        return;
      }

      // 좌표가 있으면 사용, 없으면 주소로 검색
      let center: { lat: number; lng: number };
      let mapTitle: string;

      if (lat && lng) {
        center = { lat: parseFloat(lat), lng: parseFloat(lng) };
        mapTitle = address || `${suburb || city}, ${state}`;
      } else if (address) {
        // 주소로 Geocoding (간단한 방법: iframe 사용)
        // 실제 구현에서는 Geocoding API를 사용해야 함
        const addressString = `${address}, ${suburb || city}, ${state}`;
        mapTitle = addressString;
        // 기본값으로 시드니 중심 사용 (나중에 Geocoding으로 대체)
        center = { lat: -33.8688, lng: 151.2093 };
      } else {
        // 주소 정보가 없으면 시드니 중심
        center = { lat: -33.8688, lng: 151.2093 };
        mapTitle = `${suburb || city || "Sydney"}, ${state || "NSW"}`;
      }

      const map = new Map(mapRef.current, {
        center,
        zoom: lat && lng ? 15 : 12,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
      });

      new Marker({
        position: center,
        map,
        title: mapTitle,
      });
    } catch (error) {
      console.error("Error initializing map:", error);
      setError("지도를 표시할 수 없습니다");
    }
  }, [lat, lng, address, city, suburb, state, mapsLoaded]);

  // 주소가 있으면 Google Maps 링크 생성
  const getGoogleMapsUrl = () => {
    if (lat && lng) {
      return `https://www.google.com/maps?q=${lat},${lng}`;
    }
    const addressString = address || `${suburb || city}, ${state}`;
    return `https://www.google.com/maps?q=${encodeURIComponent(addressString)}`;
  };

  return (
    <div className="w-full space-y-2">
      <div className="w-full h-64 rounded-lg overflow-hidden border relative">
        {error ? (
          <div className="flex items-center justify-center h-full bg-muted">
            <div className="text-center">
              <p className="text-muted-foreground mb-2">{error}</p>
              <a
                href={getGoogleMapsUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline text-sm"
              >
                Google Maps에서 보기
              </a>
            </div>
          </div>
        ) : (
          <>
            <div ref={mapRef} className="w-full h-full" />
            {!mapsLoaded && (
              <div className="flex items-center justify-center h-full bg-muted -mt-64 relative">
                <p className="text-muted-foreground">지도를 불러오는 중...</p>
              </div>
            )}
          </>
        )}
      </div>
      <a
        href={getGoogleMapsUrl()}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-primary hover:underline inline-block"
      >
        Google Maps에서 크게 보기 →
      </a>
    </div>
  );
}

